import React, { useCallback } from 'react';
import includes from 'lodash/includes';

import { DateType } from '../../../../../../../types';

import {
  UniversalFilterDateModes,
  UniversalFilterDateOptions,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnFilterStateChange
} from '../../UniversalFilterDate.types';
import { UniversalFilterDateIsBeforeModeOptions } from './UniversalFilterDateIsBeforeMode.types';

import {
  UniversalFilterOptionSelect,
  UniversalFilterOptionSelectOption,
  UniversalFilterOptionSelectOnChange
} from '../../../../helpers/UniversalFilterOptionSelect';

import { UniversalFilterPositiveIntegerInput } from '../../../../helpers/UniversalFilterPositiveIntegerInput';
import { UniversalFilterDateInput } from '../../../../helpers/UniversalFilterDateInput';

import { filtersKeys } from '../../../../../../../locales/keys';

const options: UniversalFilterOptionSelectOption<UniversalFilterDateIsBeforeModeOptions>[] =
  [
    {
      value: UniversalFilterDateIsBeforeModeOptions.EXACT_DAY,
      i18nText: filtersKeys.dateOptions.exactDay
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.TODAY,
      i18nText: filtersKeys.dateOptions.today
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.TOMORROW,
      i18nText: filtersKeys.dateOptions.tomorrow
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.YESTERDAY,
      i18nText: filtersKeys.dateOptions.yesterday
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.ONE_WEEK_AGO,
      i18nText: filtersKeys.dateOptions.oneWeekAgo
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.ONE_WEEK_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneWeekFromNow
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.ONE_MONTH_AGO,
      i18nText: filtersKeys.dateOptions.oneMonthAgo
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.ONE_MONTH_FROM_NOW,
      i18nText: filtersKeys.dateOptions.oneMonthFromNow
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_AGO,
      i18nText: filtersKeys.dateOptions.numberOfDaysAgo
    },
    {
      value: UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_FROM_NOW,
      i18nText: filtersKeys.dateOptions.numberOfDaysFromNow
    }
  ];

const currentMode = UniversalFilterDateModes.IS_BEFORE;

interface UniversalFilterDateIsBeforeModeProps {
  filterState: UniversalFilterDateFilterState;
  autoFocus?: boolean;
  onFilterStateChange: UniversalFilterDateOnFilterStateChange;
}

function UniversalFilterDateIsBeforeMode({
  filterState,
  autoFocus,
  onFilterStateChange
}: UniversalFilterDateIsBeforeModeProps) {
  const handleChange = useCallback<
    UniversalFilterOptionSelectOnChange<UniversalFilterDateIsBeforeModeOptions>
  >(
    (value) => {
      onFilterStateChange({ mode: currentMode, option: value });
    },
    [onFilterStateChange]
  );

  const handleDaysChange = useCallback<(value?: number) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDays: value });
    },
    [filterState, onFilterStateChange]
  );

  const handleDateChange = useCallback<(value?: DateType) => void>(
    (value) => {
      onFilterStateChange({ ...filterState, optionDate: value });
    },
    [filterState, onFilterStateChange]
  );

  const withDaysInput = includes(
    [
      UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_AGO,
      UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_FROM_NOW
    ],
    filterState.option
  );

  return (
    <>
      <div className="flex-1">
        <UniversalFilterOptionSelect<UniversalFilterDateOptions>
          options={options}
          selectedValue={filterState.option}
          onChange={handleChange}
        />
      </div>

      {filterState.option ===
      UniversalFilterDateIsBeforeModeOptions.EXACT_DAY ? (
        <UniversalFilterDateInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDate}
          autoFocus={withDaysInput ? false : autoFocus}
          onChange={handleDateChange}
        />
      ) : null}

      {withDaysInput ? (
        <UniversalFilterPositiveIntegerInput
          key={[filterState.mode, filterState.option].join('-')}
          value={filterState.optionDays}
          i18nPlaceholder={filtersKeys.enterDays}
          autoFocus={autoFocus}
          onChange={handleDaysChange}
        />
      ) : null}
    </>
  );
}

export default UniversalFilterDateIsBeforeMode;
